import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormControl, FormArray } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { IconButtonComponent } from 'src/app/_components/buttons/icon-button/icon-button.component';
import { MatFabButtonComponent } from 'src/app/_components/buttons/mat-fab-button/mat-fab-button.component';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { DropdownFormFieldComponent } from 'src/app/_components/forms/dropdown-form-field/dropdown-form-field.component';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';
import { FilterModalComponent } from 'src/app/_components/modals/filter-modal/filter-modal.component';
import { OptionTabToggleComponent } from 'src/app/_components/selection-controls/option-tab-toggle/option-tab-toggle.component';
import { clearFormArray } from 'src/app/_helpers/utils';
import { hasExperimentEditAccess } from 'src/app/_helpers/experiment-utils';
import { DeviceErrorResponse } from 'src/app/_models/api-responses';
import { DeviceError } from 'src/app/_models/device-error';
import {
  DeviceErrorType,
  DeviceErrorTypeDisplay,
} from 'src/app/_models/device-error-type';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { ExperimentService } from 'src/app/_services/experiment.service';
import { ModalService } from 'src/app/_services/modal.service';
import { DeviceService } from 'src/app/_services/device.service';
import { DataService } from 'src/app/_services/data.service';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { TabOptions } from 'src/app/_models/tab-options';
import { Role } from 'src/app/_models/role';
import { User } from 'src/app/_models/user';

@Component({
  selector: 'app-device-errors',
  templateUrl: './device-errors.component.html',
  styleUrl: './device-errors.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OptionTabToggleComponent,
    IconButtonComponent,
    MatIcon,
    MatTooltip,
    DropdownFormFieldComponent,
    ErrorPageComponent,
    MatFabButtonComponent,
    FilterModalComponent,
    LoadingSpinnerComponent,
  ],
})
export class DeviceErrorsComponent implements OnInit, OnDestroy {
  protected route = inject(ActivatedRoute);
  protected formBuilder = inject(FormBuilder);
  protected experimentService = inject(ExperimentService);
  protected modalService = inject(ModalService);
  protected deviceService = inject(DeviceService);
  protected dataService = inject(DataService);
  protected router = inject(Router);
  protected authenticationService = inject(AuthenticationService);

  unsubscribe$ = new Subject<void>();

  // Routing
  navOptions = signal<TabOptions[]>([]);
  experimentId = signal<number | null>(null);

  currentUser = signal<User | null>(null);
  // Make role enum accessible in html template
  Role = Role;
  readonly hasEditAccess = computed(() => hasExperimentEditAccess(this.currentUser()?.role));

  // Control vars
  error = signal<ErrorObject | null>(null);
  loading = signal(false);
  errorTypeIds = signal<number[]>([]);
  DeviceErrorType = DeviceErrorType;

  // Table vars
  deviceErrors = signal<DeviceError[]>([]);
  currentSort = signal<{ sortBy: string; sortDirection: string }>({
    sortBy: 'Time',
    sortDirection: 'asc',
  });

  // Pagination
  pageRequested = signal(1);
  pageSize = signal(25);
  totalPages = signal(0);
  currentPage = signal(1);
  hasNext = signal(false);
  hasPrevious = signal(false);

  // Pagination form controls
  requestsPerPageField = new FormBase({
    key: 'requestsPerPage',
    label: 'Page size',
    type: 'dropdown',
    placeholder: '',
    disabled: false,
    required: false,
    value: '25',
    options: [
      { key: '5', value: '5' },
      { key: '10', value: '10' },
      { key: '25', value: '25' },
      { key: '50', value: '50' },
      { key: '100', value: '100' },
    ],
  });

  errorTypeFilterField = new FormBase({
    key: 'errorTypeFilter',
    label: 'Filter by error type:',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      {
        key: DeviceErrorType.Overheating,
        value: DeviceErrorTypeDisplay[DeviceErrorType.Overheating],
      },
      {
        key: DeviceErrorType.USBWriteFail,
        value: DeviceErrorTypeDisplay[DeviceErrorType.USBWriteFail],
      },
      {
        key: DeviceErrorType.TurbidostatFailedToControlOD,
        value:
          DeviceErrorTypeDisplay[DeviceErrorType.TurbidostatFailedToControlOD],
      },
      {
        key: DeviceErrorType.ThermalFuseBlown,
        value: DeviceErrorTypeDisplay[DeviceErrorType.ThermalFuseBlown],
      },
      {
        key: DeviceErrorType.TemperatureSensorErrorInFlask,
        value:
          DeviceErrorTypeDisplay[DeviceErrorType.TemperatureSensorErrorInFlask],
      },
      {
        key: DeviceErrorType.TemperatureSensorErrorOnHeater,
        value:
          DeviceErrorTypeDisplay[
            DeviceErrorType.TemperatureSensorErrorOnHeater
          ],
      },
    ],
  });

  paginationControls = this.formBuilder.group({
    [this.requestsPerPageField.key]: new FormControl(
      this.requestsPerPageField.value,
    ),
  });

  filterForm = this.formBuilder.group({
    [this.errorTypeFilterField.key]: new FormArray([]),
  });

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((params: Params) => {
        this.experimentId.set(parseInt(params.id as string, 10));
        this.navOptions.set([
          {
            value: 'display',
            labelText: 'Display',
            link: '/experiment/' + this.experimentId().toString() + '/display',
          },
          {
            value: 'control',
            labelText: 'Control',
            link: '/experiment/' + this.experimentId().toString() + '/control',
          },
          {
            value: 'device-errors',
            labelText: 'Device Errors',
            link:
              '/experiment/' +
              this.experimentId().toString() +
              '/device-errors',
          },
        ]);
      });

    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser.set(user);
      });

    if (this.experimentService.sort !== null) {
      this.currentSort.set(this.experimentService.sort);
    }

    if (this.experimentService.deviceErrorPaginationCache !== null) {
      this.pageSize.set(
        this.experimentService.deviceErrorPaginationCache.pageSize,
      );
      this.pageRequested.set(
        this.experimentService.deviceErrorPaginationCache.pageNumber,
      );
      this.paginationControls.patchValue({
        [this.requestsPerPageField.key]: this.pageSize().toString(),
      });
    }

    // Now get device error logs
    this.getExperimentDeviceErrorLog();
    this.initialiseDeviceCheckboxes();

    // Subscribe to the value changes of the user requests per page dropdown
    this.paginationControls
      .get(this.requestsPerPageField.key)
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((numOfPages) => {
        this.pageSize.set(Number(numOfPages));
        // Also reset the page number to 1 as we are changing the pagination range
        this.pageRequested.set(1);
        this.getExperimentDeviceErrorLog();
      });
  }

  resetCheckboxes(): void {
    const resetValues = this.checkboxesFormArray.controls.map(() => false);
    this.checkboxesFormArray.patchValue(resetValues);
    this.filterForm.markAsPristine();
    this.filterForm.markAsUntouched();
  }

  get checkboxesFormArray(): FormArray {
    return this.filterForm.get('errorTypeFilter') as FormArray;
  }

  initialiseDeviceCheckboxes(): void {
    clearFormArray(this.checkboxesFormArray);
    this.errorTypeFilterField.options.forEach(() => {
      const control = this.formBuilder.control(false);
      this.checkboxesFormArray.push(control);
    });
  }

  handleDeviceCheckbox(event: Event): void {
    const target = event.target as HTMLInputElement;
    const errorType = parseInt(target.name, 10);
    if (target.checked) {
      this.errorTypeIds.update((ids) => [...ids, errorType]);
    } else {
      this.errorTypeIds.update((ids) => ids.filter((x) => x !== errorType));
    }
  }

  getExperimentDeviceErrorLog(): void {
    this.error.set(null);
    this.loading.set(true);

    this.experimentService
      .getExperimentDeviceErrorLog(
        this.experimentId(),
        this.currentSort().sortBy,
        this.currentSort().sortDirection,
        this.pageSize(),
        this.pageRequested(),
        this.errorTypeIds().length > 0 ? this.errorTypeIds() : null,
      )
      .subscribe({
        next: (response: DeviceErrorResponse) => {
          this.error.set(null);
          this.deviceErrors.set(response.deviceErrorsList);
          // Set pagination vars from response headers
          this.currentPage.set(response.paginationData.CurrentPage);
          this.pageSize.set(response.paginationData.PageSize);
          this.totalPages.set(response.paginationData.TotalPages);
          this.hasNext.set(response.paginationData.HasNext);
          this.hasPrevious.set(response.paginationData.HasPrevious);
          this.experimentService.onUpdateDeviceErrorPageNumber(
            this.currentPage(),
          );
          this.experimentService.onUpdateDeviceErrorPageSize(this.pageSize());
          this.loading.set(false);
        },
        error: (error: ErrorObject) => {
          this.error.set(error);
          this.loading.set(false);
        },
      });
  }

  getPrevious(): void {
    this.pageRequested.set(this.currentPage() - 1);
    this.getExperimentDeviceErrorLog();
  }

  getNext(): void {
    this.pageRequested.set(this.currentPage() + 1);
    this.getExperimentDeviceErrorLog();
  }

  onSort(sortTerm: string): void {
    if (this.currentSort().sortBy === sortTerm) {
      this.currentSort.update((s) => ({
        ...s,
        sortDirection: s.sortDirection === 'asc' ? 'desc' : 'asc',
      }));
    } else {
      this.currentSort.set({ sortBy: sortTerm, sortDirection: 'asc' });
    }
    this.getExperimentDeviceErrorLog();
  }

  filterModalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'reset-button':
        this.errorTypeIds.set([]);
        this.resetCheckboxes();
        this.getExperimentDeviceErrorLog();
        break;
      case 'continue-button':
        this.getExperimentDeviceErrorLog();
        this.closeModal('filter');
        break;
    }
  }

  closeModal(modal: string): void {
    this.modalService.close(modal);
  }

  openModal(modal: string): void {
    this.modalService.open(modal);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
